import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { NumberFormatterService, NumberFormatConfig } from '../../screen-engine/services/number-formatter.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

export interface NumberFormatModalConfig {
  decimalPlaces: number;
  formatThousands: boolean;
  thousandSeparator: '.' | ',';
  decimalSeparator: '.' | ',';
  addonBefore: string;
  addonAfter: string;
}

@Component({
  selector: 'app-number-format-modal',
  templateUrl: './number-format-modal.component.html',
  styleUrls: ['./number-format-modal.component.scss'],
  standalone: false
})
export class NumberFormatModalComponent implements OnInit, OnDestroy {
  form: FormGroup;
  initialConfig: NumberFormatModalConfig = {
    decimalPlaces: 2,
    formatThousands: false,
    thousandSeparator: '.',
    decimalSeparator: ',',
    addonBefore: '',
    addonAfter: ''
  };

  // Valor de exemplo para preview
  previewValue: number = 1234567.89;
  previewResult: string = '';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modal: NzModalRef,
    private numberFormatter: NumberFormatterService
  ) {
    this.form = this.fb.group({
      decimalPlaces: [2, [Validators.required, Validators.min(0), Validators.max(10)]],
      formatThousands: [false],
      thousandSeparator: ['.'],
      decimalSeparator: [','],
      addonBefore: [''],
      addonAfter: ['']
    });
  }

  ngOnInit(): void {
    // Popula com configuração existente
    if (this.initialConfig) {
      this.form.patchValue({
        decimalPlaces: this.initialConfig.decimalPlaces ?? 2,
        formatThousands: this.initialConfig.formatThousands ?? false,
        thousandSeparator: this.initialConfig.thousandSeparator ?? '.',
        decimalSeparator: this.initialConfig.decimalSeparator ?? ',',
        addonBefore: this.initialConfig.addonBefore ?? '',
        addonAfter: this.initialConfig.addonAfter ?? ''
      });
    }

    // Atualiza preview inicial
    this.updatePreview();

    // Atualiza preview quando form muda
    this.form.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(100)
      )
      .subscribe(() => {
        this.validateSeparators();
        this.updatePreview();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Garante que separador de milhar e decimal não sejam iguais
   */
  private validateSeparators(): void {
    const thousand = this.form.get('thousandSeparator')?.value;
    const decimal = this.form.get('decimalSeparator')?.value;
    const formatThousands = this.form.get('formatThousands')?.value;

    // Só valida se formatação de milhar está ativa
    if (formatThousands && thousand === decimal) {
      // Inverte o separador decimal
      const newDecimal = decimal === '.' ? ',' : '.';
      this.form.get('decimalSeparator')?.setValue(newDecimal, { emitEvent: false });
    }
  }

  /**
   * Atualiza o preview com a formatação atual
   */
  private updatePreview(): void {
    const config: NumberFormatConfig = {
      decimalPlaces: this.form.get('decimalPlaces')?.value ?? 2,
      formatThousands: this.form.get('formatThousands')?.value ?? false,
      thousandSeparator: this.form.get('thousandSeparator')?.value ?? '.',
      decimalSeparator: this.form.get('decimalSeparator')?.value ?? ','
    };

    const formatted = this.numberFormatter.format(this.previewValue, config);
    const prefix = this.form.get('addonBefore')?.value || '';
    const suffix = this.form.get('addonAfter')?.value || '';

    // Monta preview com prefixo e sufixo
    let result = formatted;
    if (prefix) {
      result = prefix + ' ' + result;
    }
    if (suffix) {
      result = result + ' ' + suffix;
    }

    this.previewResult = result;
  }

  /**
   * Confirma e fecha o modal retornando a configuração
   */
  handleOk(): void {
    if (this.form.valid) {
      const result: NumberFormatModalConfig = {
        decimalPlaces: this.form.get('decimalPlaces')?.value,
        formatThousands: this.form.get('formatThousands')?.value,
        thousandSeparator: this.form.get('thousandSeparator')?.value,
        decimalSeparator: this.form.get('decimalSeparator')?.value,
        addonBefore: this.form.get('addonBefore')?.value || '',
        addonAfter: this.form.get('addonAfter')?.value || ''
      };
      this.modal.close(result);
    }
  }

  /**
   * Cancela e fecha o modal sem retornar dados
   */
  handleCancel(): void {
    this.modal.close();
  }
}
